import { useEffect, useRef, useState } from "react";

interface LiveLogStreamProps {
  lines: string[];
  /** True while the subprocess is still producing output. */
  running: boolean;
  /** Max rendered height in px (stream scrolls inside). */
  maxHeight?: number;
}

/**
 * Auto-scrolling, bottom-faded log panel for installer output.
 *
 * Behaviour:
 *  - Scrolls to bottom on new lines *only if* the user hasn't scrolled up.
 *  - Shows a "Jump to latest" pill if the user has scrolled away from the tail.
 *  - Fades the very last line with a subtle bottom mask so it reads as
 *    "flowing", not "clipped".
 */
export function LiveLogStream({
  lines,
  running,
  maxHeight = 192,
}: LiveLogStreamProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);

  // Track whether the user has scrolled up from the tail.
  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    setPinnedToBottom(distance < 24);
  };

  // Auto-scroll on new lines when pinned.
  useEffect(() => {
    if (!pinnedToBottom) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length, pinnedToBottom]);

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setPinnedToBottom(true);
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="overflow-y-auto rounded-xl border border-space-30 bg-space-20/60 px-4 py-3 font-mono text-[12px] leading-[1.6] text-space-50"
        style={{ maxHeight }}
      >
        {lines.length === 0 ? (
          <p className="text-space-40">Waiting for output…</p>
        ) : (
          lines.map((line, i) => (
            <div
              key={i}
              className="whitespace-pre-wrap break-all"
              style={{
                animation: i > lines.length - 8 ? "onbFadeIn 140ms ease-out both" : undefined,
              }}
            >
              {line || "\u00A0"}
            </div>
          ))
        )}
      </div>

      {/* Bottom fade mask */}
      {running && (
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 h-6 rounded-b-xl"
          style={{
            background:
              "linear-gradient(to top, rgba(248,248,248,0.95), rgba(248,248,248,0))",
          }}
        />
      )}

      {/* Jump-to-latest pill */}
      {!pinnedToBottom && (
        <button
          onClick={jumpToLatest}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-space px-3 py-1 text-[12px] font-medium tracking-[0.04em] text-white shadow-md transition-all hover:bg-space-50 active:scale-95"
        >
          Jump to latest ↓
        </button>
      )}
    </div>
  );
}
